import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Column,
  DataTable,
  Filter,
  FilterDefinition,
  QueryParams,
} from '../../components/DataTable';
import { AuthContext } from '../../context/AuthContext';
import { ApiService } from '../../services/api';
import { Task, TasksSearchParams } from '../../types';

const TABLE_CONFIG = {
  PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100] as number[],
} as const;

interface TasksQueryParams {
  chainId: number | null;
  storeId: number | null;
  tagNamePrefix: string | null;
  reviewStatus: 'reviewed' | 'non_reviewed' | null;
  taskDate: string | null;
  isActive: boolean | null;
  isAudited: boolean | null;
}

export const TasksPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);
  const cachedTasksRef = useRef<Task[]>([]);

  // Parse query parameters into structured data
  const queryParams = useMemo<TasksQueryParams>(() => {
    const chainIdParam = searchParams.get('chain_id');
    const storeIdParam = searchParams.get('store_id');
    const tagNamePrefixParam = searchParams.get('tag_name_prefix');
    const reviewStatusParam = searchParams.get('review_status');
    const taskDateParam = searchParams.get('task_date');
    const isActiveParam = searchParams.get('is_active');
    const isAuditedParam = searchParams.get('is_audited');

    return {
      chainId: chainIdParam ? parseInt(chainIdParam, 10) : null,
      storeId: storeIdParam ? parseInt(storeIdParam, 10) : null,
      tagNamePrefix: tagNamePrefixParam,
      reviewStatus: reviewStatusParam as 'reviewed' | 'non_reviewed' | null,
      taskDate: taskDateParam,
      isActive: isActiveParam ? isActiveParam === 'true' : null,
      isAudited: isAuditedParam ? isAuditedParam === 'true' : null,
    };
  }, [searchParams]);

  // Convert query params to DataTable filters
  const dataTableFilters = useMemo<Filter[]>(() => {
    const filters: Filter[] = [];
    if (queryParams.chainId !== null) {
      filters.push({
        field: 'chainId',
        value: queryParams.chainId.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.storeId !== null) {
      filters.push({
        field: 'storeId',
        value: queryParams.storeId.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.tagNamePrefix) {
      filters.push({
        field: 'tagNamePrefix',
        value: queryParams.tagNamePrefix,
        operator: 'contains',
      });
    }
    if (queryParams.reviewStatus) {
      filters.push({
        field: 'reviewStatus',
        value: queryParams.reviewStatus,
        operator: 'equals',
      });
    }
    if (queryParams.taskDate) {
      filters.push({
        field: 'taskDate',
        value: queryParams.taskDate,
        operator: 'equals',
      });
    }
    if (queryParams.isActive !== null) {
      filters.push({
        field: 'isActive',
        value: queryParams.isActive.toString(),
        operator: 'equals',
      });
    }
    if (queryParams.isAudited !== null) {
      filters.push({
        field: 'isAudited',
        value: queryParams.isAudited.toString(),
        operator: 'equals',
      });
    }

    return filters;
  }, [queryParams]);

  const validateFilter = useCallback((filter: Filter) => {
    if (filter.field === 'taskDate' && filter.value) {
      return /^\d{4}-\d{2}-\d{2}$/.test(filter.value);
    }

    return true;
  }, []);

  // Update URL when filters change
  const handleFiltersChange = useCallback(
    (filters: Filter[]) => {
      const newParams = new URLSearchParams(searchParams);

      // Clear filter-related params
      newParams.delete('chain_id');
      newParams.delete('store_id');
      newParams.delete('tag_name_prefix');
      newParams.delete('review_status');
      newParams.delete('task_date');
      newParams.delete('is_active');
      newParams.delete('is_audited');

      // Add new filter params
      filters.forEach((filter) => {
        if (filter.field === 'chainId' && filter.value) {
          newParams.set('chain_id', filter.value);
        } else if (filter.field === 'storeId' && filter.value) {
          newParams.set('store_id', filter.value);
        } else if (filter.field === 'tagNamePrefix' && filter.value) {
          newParams.set('tag_name_prefix', filter.value);
        } else if (filter.field === 'reviewStatus' && filter.value) {
          newParams.set('review_status', filter.value);
        } else if (filter.field === 'taskDate' && filter.value) {
          if (/^\d{4}-\d{2}-\d{2}$/.test(filter.value)) {
            newParams.set('task_date', filter.value);
          }
        } else if (filter.field === 'isActive' && filter.value) {
          newParams.set('is_active', filter.value);
        } else if (filter.field === 'isAudited' && filter.value) {
          newParams.set('is_audited', filter.value);
        }
      });

      if (newParams.toString() !== searchParams.toString()) {
        setSearchParams(newParams);
      }
    },
    [searchParams, setSearchParams]
  );

  // Define async data fetcher for DataTable
  const fetchTasks = useCallback(
    async (params: QueryParams) => {
      setError(null);

      if (!token || !queryParams.chainId) {
        return { data: [], total: 0 };
      }

      try {
        // Build filters from DataTable
        const storeIdFilter = params.filters?.find((f) => f.field === 'storeId');
        const tagNamePrefixFilter = params.filters?.find((f) => f.field === 'tagNamePrefix');
        const reviewStatusFilter = params.filters?.find((f) => f.field === 'reviewStatus');
        const taskDateFilter = params.filters?.find((f) => f.field === 'taskDate');
        const isActiveFilter = params.filters?.find((f) => f.field === 'isActive');
        const isAuditedFilter = params.filters?.find((f) => f.field === 'isAudited');

        if (!taskDateFilter?.value) {
          return { data: cachedTasksRef.current, total: cachedTasksRef.current.length };
        }

        const taskDate = dayjs(taskDateFilter.value);
        if (!taskDate.isValid() || taskDate.isAfter(dayjs().startOf('day'))) {
          return { data: [], total: 0 };
        }

        // Map column fields to valid API sort fields
        const sortFieldMap: Record<string, 'pog_compliance_score'> = {
          pogComplianceScore: 'pog_compliance_score',
        };

        // Get the mapped sort field, only pogComplianceScore is sortable
        const sortField = params.orderBy ? sortFieldMap[params.orderBy] || undefined : undefined;

        const apiParams: TasksSearchParams = {
          chainId: queryParams.chainId,
          ...(storeIdFilter?.value && {
            storeId: parseInt(storeIdFilter.value),
          }),
          ...(tagNamePrefixFilter?.value && {
            tagNamePrefix: tagNamePrefixFilter.value,
          }),
          ...(reviewStatusFilter?.value && {
            reviewStatus: reviewStatusFilter.value as 'reviewed' | 'non_reviewed',
          }),
          ...(taskDateFilter?.value && {
            taskDate: taskDateFilter.value,
          }),
          ...(isActiveFilter?.value && {
            isActive: isActiveFilter.value === 'true',
          }),
          ...(isAuditedFilter?.value && {
            isAudited: isAuditedFilter.value === 'true',
          }),
          ...(sortField && { sortField }),
          ...(params.orderDirection && {
            sortOrder: params.orderDirection as 'asc' | 'desc',
          }),
          page: params.page + 1, // API uses 1-based pagination
          limit: params.pageSize,
        };

        const data = await ApiService.searchTasks(token, apiParams);
        cachedTasksRef.current = data;

        // Store tasks with chain_id in localStorage
        if (data.length > 0) {
          const tasksWithMetadata = {
            chainId: queryParams.chainId,
            tasks: data,
          };
          localStorage.setItem('tasksList', JSON.stringify(tasksWithMetadata));
        }

        return { data, total: data.length }; // Note: API might need to return total count separately
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch tasks');
        return { data: [], total: 0 };
      }
    },
    [queryParams.chainId, token]
  );

  const columns: Column<Task>[] = [
    {
      field: 'chainName',
      title: 'Chain name',
      sortable: false,
      width: '150px',
    },
    {
      field: 'storeName',
      title: 'Store name',
      sortable: false,
      width: '150px',
    },
    {
      field: 'storeId',
      title: 'Store ID',
      sortable: false,
      width: '100px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'cameraId',
      title: 'Camera ID',
      sortable: false,
      width: '100px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'imageId',
      title: 'Image ID',
      sortable: false,
      width: '120px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'imageCaptureTime',
      title: 'Image capture time',
      sortable: false,
      width: '180px',
      render: (value: string) => {
        const date = new Date(value);
        return <span className="text-xs">{date.toLocaleString()}</span>;
      },
    },
    {
      field: 'tagName',
      title: 'Tag name',
      sortable: false,
      width: '120px',
      render: (value: string) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'actionTaskId',
      title: 'Task ID',
      sortable: false,
      width: '130px',
      render: (value: number) => <span className="text-xs font-mono">{value}</span>,
    },
    {
      field: 'isActive',
      title: 'Is task active?',
      sortable: false,
      width: '120px',
      render: (value: boolean) => (
        <span
          className={`px-2 py-1 text-xs rounded ${
            value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {value ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      field: 'isAudited',
      title: (
        <span className="flex items-center gap-1">
          Is worked
          <Tooltip title="Highlights tasks created from an AI Audit" arrow>
            <InfoIcon className="text-gray-500 cursor-help" fontSize="small" />
          </Tooltip>
        </span>
      ),
      sortable: false,
      width: '120px',
      render: (value: boolean) => (
        <span
          className={`px-2 py-1 text-xs rounded ${
            value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {value ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      field: 'pogComplianceScore',
      title: 'POG compliance score',
      sortable: true,
      width: '160px',
      render: (value: number) => <span className="text-xs">{value.toFixed(2)}</span>,
    },
    {
      field: 'osaComplianceScore',
      title: 'OSA score',
      sortable: false,
      width: '120px',
      render: (value: number) => <span className="text-xs">{value.toFixed(2)}</span>,
    },
    {
      field: 'numberOfSubtasks',
      title: 'Number of subtasks',
      sortable: false,
      width: '150px',
      render: (value: number) => <span className="text-xs">{value}</span>,
    },
    {
      field: 'taskCreatedAt',
      title: 'Task created at',
      sortable: false,
      width: '180px',
      render: (value: string) => {
        const date = new Date(value);
        return <span className="text-xs">{date.toLocaleString()}</span>;
      },
    },
  ];

  // Define custom filters based on searchTasks API parameters
  const filterDefinitions: FilterDefinition[] = [
    {
      field: 'chainId',
      title: 'Chain ID',
      type: 'number',
      placeholder: 'Enter chain ID (required)',
    },
    {
      field: 'storeId',
      title: 'Store ID',
      type: 'number',
      placeholder: 'Enter store ID',
    },
    {
      field: 'tagNamePrefix',
      title: 'Tag Name Prefix',
      type: 'text',
      placeholder: 'Search by tag name prefix...',
    },
    {
      field: 'reviewStatus',
      title: 'Review Status',
      type: 'select',
      placeholder: 'Select review status',
      options: [
        { label: 'Reviewed', value: 'reviewed' },
        { label: 'Non-reviewed', value: 'non_reviewed' },
      ],
    },
    {
      field: 'taskDate',
      title: 'Task Date',
      type: 'date',
      placeholder: 'Select date',
    },
    {
      field: 'isActive',
      title: 'Is Active',
      type: 'select',
      placeholder: 'Select status',
      options: [
        { label: 'Active', value: 'true' },
        { label: 'Inactive', value: 'false' },
      ],
    },
    {
      field: 'isAudited',
      title: 'Is worked',
      type: 'select',
      placeholder: 'Select status',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
    },
  ];

  // Build comprehensive title with all applied filters
  const dataTableTitle = useMemo(() => {
    const parts: string[] = ['Planogram Compliance Tasks'];
    const filters: string[] = [];

    if (queryParams.chainId !== null) {
      filters.push(`Chain: ${queryParams.chainId}`);
    }
    if (queryParams.storeId !== null) {
      filters.push(`Store: ${queryParams.storeId}`);
    }
    if (queryParams.tagNamePrefix) {
      filters.push(`Tag: ${queryParams.tagNamePrefix}`);
    }
    if (queryParams.reviewStatus) {
      filters.push(`Review: ${queryParams.reviewStatus}`);
    }
    if (queryParams.taskDate) {
      filters.push(`Date: ${queryParams.taskDate}`);
    }
    if (queryParams.isActive !== null) {
      filters.push(`Active: ${queryParams.isActive ? 'Yes' : 'No'}`);
    }
    if (queryParams.isAudited !== null) {
      filters.push(`Worked: ${queryParams.isAudited ? 'Yes' : 'No'}`);
    }

    if (filters.length > 0) {
      parts.push(`(${filters.join(', ')})`);
    }

    return parts.join(' ');
  }, [queryParams]);

  return (
    <div className="h-screen bg-gray-50 p-4 flex flex-col">
      <div className="w-full mx-auto space-y-4 flex flex-col flex-1 min-h-0">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            Error: {error}
          </div>
        )}

        {(!queryParams.chainId || !queryParams.taskDate) && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
            {!queryParams.chainId && !queryParams.taskDate
              ? 'Please provide chain_id parameter in the URL and select a Task Date to fetch tasks.'
              : !queryParams.chainId
                ? 'Please provide chain_id parameter in the URL to fetch tasks.'
                : 'Please select a Task Date to fetch tasks.'}
          </div>
        )}

        <div className="flex-1 min-h-0">
          <DataTable<Task>
            title={dataTableTitle}
            data={fetchTasks}
            columns={columns}
            pageSize={TABLE_CONFIG.PAGE_SIZE}
            pageSizeOptions={TABLE_CONFIG.PAGE_SIZE_OPTIONS}
            searchable={false}
            filterable={true}
            filterDefinitions={filterDefinitions}
            filterValidator={validateFilter}
            initialFilters={dataTableFilters}
            onFiltersChange={handleFiltersChange}
            tableKey="tasks-table"
            infinitePagination={true}
            onRowClick={(row: Task) => {
              const params = new URLSearchParams();
              params.set('store_name', row.storeName);
              params.set('chain_id', row.chainId.toString());
              params.set('store_id', row.storeId.toString());
              params.set('cameraId', row.cameraId.toString());
              params.set('tag_name', row.tagName);
              params.set('image_id', row.imageId.toString());
              params.set('created_at', row.taskCreatedAt);
              navigate(`/tasks/${row.actionTaskId}?${params.toString()}`);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default TasksPage;
